"use client";

import React from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  useCarousel,
} from "@/components/ui/carousel";

export interface StudentTestimonial {
  id: number;
  name: string;
  university: string;
  rating: number;
  review: string;
  image: string;
}

export const STUDENT_TESTIMONIALS: StudentTestimonial[] = [
  {
    id: 1,
    name: "Ananya Sharma",
    university: "University of Portsmouth",
    rating: 4,
    review:
      "I was looking for one particular course but couldn't find many good options. On Edvia I found a similar course at University of Portsmouth which I hadn't considered before. Now it's on my shortlist.",
    image: "/assets/images/png/testimonial/student1.png",
  },
  {
    id: 2,
    name: "Rohan Mehta",
    university: "University of Greenwich",
    rating: 5,
    review:
      "I was comparing universities mainly on fees and course options. Edvia made it easier because I could check the details without opening different websites for every university.",
    image: "/assets/images/png/testimonial/student2.png",
  },
  {
    id: 3,
    name: "Priya Verma",
    university: "Coventry University",
    rating: 4,
    review:
      "I wasn't sure which universities would accept my profile and what courses I could actually apply for. I used Edvia to check different options and found a course at Coventry University.",
    image: "/assets/images/png/testimonial/student3.png",
  },
  {
    id: 4,
    name: "Aditya Singh",
    university: "Glasgow Caledonian University",
    rating: 5,
    review:
      "I was confused between UK, Germany and a few other countries. Instead of deciding the country first, I searched for my course on Edvia and then looked at the universities.",
    image: "/assets/images/png/testimonial/student4.png",
  },
  {
    id: 5,
    name: "Nafeesa",
    university: "University of Portsmouth",
    rating: 4,
    review:
      "I had already checked so many university websites that everything was starting to look the same. On Edvia I searched for my course first and compared the universities. Much easier on Edvia.",
    image: "/assets/images/png/testimonial/student5.png",
  },
  {
    id: 6,
    name: "Karan Patel",
    university: "Leeds Beckett University",
    rating: 5,
    review:
      "I honestly had no idea which universities offered the course I wanted. I searched on Edvia and got a few options to compare. University of Greenwich was one I hadn't really considered before.",
    image: "/assets/images/png/testimonial/student6.png",
  },
  {
    id: 7,
    name: "Neha Gupta",
    university: "Durham University",
    rating: 5,
    review:
      "For me the main problem was fees. I had shortlisted a few universities but wasn't able to compare them properly. I used Edvia to check the course and fee details and it helped me narrow down my choices.",
    image: "/assets/images/png/testimonial/student7.png",
  },
  {
    id: 8,
    name: "Arjun Malhotra",
    university: "University of Leeds",
    rating: 5,
    review:
      "I was using Google, university websites and random blogs to find courses. Had way too many tabs open. Edvia made it simpler to search for the course first.",
    image: "/assets/images/png/testimonial/student8.png",
  },
];

/** Mobile prev/next — must live inside <Carousel> to access context */
function MobileControls() {
  const { scrollPrev, scrollNext } = useCarousel();
  return (
    <div className="flex items-center justify-center gap-3 mt-6 sm:hidden">
      <button
        onClick={scrollPrev}
        className="w-9 h-9 rounded-full bg-[#EBF3FF] active:bg-[#D9E9FF] text-[#1776F0] flex items-center justify-center shadow-sm cursor-pointer"
        aria-label="Previous slide"
      >
        <ChevronLeft className="size-4" strokeWidth={2.5} />
      </button>
      <button
        onClick={scrollNext}
        className="w-9 h-9 rounded-full bg-[#EBF3FF] active:bg-[#D9E9FF] text-[#1776F0] flex items-center justify-center shadow-sm cursor-pointer"
        aria-label="Next slide"
      >
        <ChevronRight className="size-4" strokeWidth={2.5} />
      </button>
    </div>
  );
}

export default function StudentStories() {
  return (
    <div className="relative bg-center bg-cover bg-no-repeat overflow-hidden">
      {/*
       * BANDWIDTH FIX: Previously the background was set via a Tailwind
       * arbitrary bg-url utility class, which bypasses Next.js image
       * optimisation — the full 2.9 MB PNG was served to every visitor
       * on the homepage AND every country page.
       *
       * Replaced with a next/image fill + object-cover layer so Next.js
       * can serve WebP/AVIF (typically 60-80% smaller) and apply proper
       * caching headers automatically.
       */}
      <Image
        src="/assets/images/png/testimonial/testimonialBg.webp"
        alt=""
        fill
        className="object-cover object-center -z-0"
        aria-hidden="true"
        loading="lazy"
        sizes="100vw"
      />

      {/* Top-left quote */}
      <div className="absolute top-0 left-0 sm:top-4 sm:left-6 lg:left-12 select-none pointer-events-none z-10" aria-hidden="true">
        <Image
          src="/assets/images/png/testimonial/leftQuote.png"
          alt=""
          width={200}
          height={200}
          className="w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] md:w-[180px] md:h-[180px] lg:w-[220px] lg:h-[220px] object-contain"
          priority={false}
        />
      </div>

      {/* Bottom-right quote */}
      <div className="absolute bottom-[90px] right-0 sm:right-6 lg:bottom-[90px] lg:right-12 select-none pointer-events-none z-0" aria-hidden="true">
        <Image
          src="/assets/images/png/testimonial/rightQuote.png"
          alt=""
          width={200}
          height={200}
          className="w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] md:w-[180px] md:h-[180px] lg:w-[220px] lg:h-[220px] object-contain"
          priority={false}
        />
      </div>

      <div className="container max-w-[1400px] mx-auto pb-[18px] md:pb-8 lg:pb-[313px] pt-[18px] md:pt-8 lg:pt-[96px] px-4 xxs:px-6 sm:px-[40px] relative z-20">
        <div className="container 2xl:max-w-[1440px] mx-auto px-4 sm:px-6 relative z-10">

          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-[34px] font-bold text-[#191733] tracking-tight">
              Students Who Started With Edvia
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-[#6B7280] ff_poppins font-normal mt-2">
              Real stories from students exploring study abroad
            </p>
          </div>

          {/* Carousel — outer div clips mobile overflow */}
          <div className="overflow-hidden">
            <Carousel
              opts={{ align: "center", loop: true }}
              className="w-full max-w-[1040px] mx-auto"
            >
              <CarouselContent
                className="-ml-3 sm:-ml-5"
                containerClassName="overflow-visible sm:overflow-hidden"
              >
                {STUDENT_TESTIMONIALS.map((item) => (
                  <CarouselItem
                    key={item.id}
                    className="pl-3 sm:pl-5 basis-[85%] sm:basis-1/2 lg:basis-1/3"
                  >
                    {/* Card */}
                    <div className="w-full h-full bg-white rounded-[24px] sm:rounded-[28px] p-5 sm:p-6 shadow-[0_8px_25px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.1)] transition-all duration-300 flex flex-col items-center text-center">
                      {/* Avatar */}
                      <div className="w-[78px] h-[78px] sm:w-[84px] sm:h-[84px] rounded-full mx-auto mb-3 relative overflow-hidden shrink-0">
                        <Image
                          src={item.image}
                          alt={item.name}
                          fill
                          className="object-cover"
                          sizes="84px"
                        />
                      </div>

                      {/* Author Name */}
                      <span className="text-[12px] sm:text-[13px] font-medium text-[#9CA3AF] ff_poppins">
                        {item.name}
                      </span>

                      {/* University */}
                      <h3 className="text-[14px] sm:text-[15px] font-bold text-[#191733] ff_poppins mt-0.5 mb-1.5 line-clamp-1">
                        {item.university}
                      </h3>

                      {/* Star Rating */}
                      <div className="flex items-center justify-center gap-1 mb-2.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`size-[15px] ${
                              i < item.rating
                                ? "fill-[#F59E0B] text-[#F59E0B]"
                                : "fill-gray-200 text-gray-200"
                            }`}
                          />
                        ))}
                      </div>

                      {/* Quote Text */}
                      <p className="text-[12px] sm:text-[13px] leading-[20px] sm:leading-[22px] text-[#4B5563] text-center ff_poppins font-normal">
                        &ldquo;{item.review}&rdquo;
                      </p>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {/* Desktop prev/next */}
              <CarouselPrevious className="hidden sm:flex -left-3 md:-left-5 w-9 h-9 md:w-10 md:h-10 bg-[#EBF3FF] hover:bg-[#D9E9FF] text-[#1776F0] border-none shadow-[0_2px_10px_rgba(23,118,240,0.15)] hover:scale-105 transition-all" />
              <CarouselNext className="hidden sm:flex -right-3 md:-right-5 w-9 h-9 md:w-10 md:h-10 bg-[#EBF3FF] hover:bg-[#D9E9FF] text-[#1776F0] border-none shadow-[0_2px_10px_rgba(23,118,240,0.15)] hover:scale-105 transition-all" />

              {/* Mobile prev/next */}
              <MobileControls />
            </Carousel>
          </div>

        </div>
      </div>
    </div>
  );
}
